import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ordersAPI } from '../../api/ordersAPI';

export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.getOrders();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch orders');
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.getOrderById(orderId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch order');
    }
  }
);

export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.createOrder(orderData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create order');
    }
  }
);

export const checkoutOrder = createAsyncThunk(
  'orders/checkoutOrder',
  async ({ orderId, checkoutData }, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.checkoutOrder(orderId, checkoutData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to checkout order');
    }
  }
);

export const submitRating = createAsyncThunk(
  'orders/submitRating',
  async ({ orderItemId, matchesDescription }, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.submitRating(orderItemId, matchesDescription);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to submit rating');
    }
  }
);

export const submitFeedback = createAsyncThunk(
  'orders/submitFeedback',
  async ({ orderItemId, itemAsDescribed, sellerResponsiveness }, { rejectWithValue }) => {
    try {
      const response = await ordersAPI.submitFeedback(
        orderItemId,
        itemAsDescribed,
        sellerResponsiveness
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to submit feedback');
    }
  }
);

export const submitItemAcceptance = createAsyncThunk(
  'orders/submitItemAcceptance',
  async (
    {
      orderItemId,
      accepted,
      matchesDescription,
      rejectionReason,
      orderId,
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await ordersAPI.submitItemAcceptance(orderItemId, {
        accepted,
        matchesDescription,
        rejectionReason,
      });
      if (orderId) {
        dispatch(fetchOrderById(orderId));
      }
      return response;
    } catch (error) {
      const data = error?.response?.data;
      const message = data?.error
        || (typeof data === 'object' && data ? Object.values(data).flat().join(' ') : null)
        || 'Failed to submit decision';
      return rejectWithValue(message);
    }
  }
);

export const submitDoorPreference = createAsyncThunk(
  'orders/submitDoorPreference',
  async (
    {
      orderItemId,
      accepted,
      rejectionReason,
      orderId,
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await ordersAPI.submitDoorPreference(orderItemId, {
        accepted,
        rejectionReason,
      });
      if (orderId) {
        dispatch(fetchOrderById(orderId));
      }
      return response;
    } catch (error) {
      const data = error?.response?.data;
      const message = data?.error
        || (typeof data === 'object' && data ? Object.values(data).flat().join(' ') : null)
        || 'Failed to save your decision';
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  orders: [],
  currentOrder: null,
  loading: false,
  error: null,
};

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Order by ID
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Order
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.unshift(action.payload);
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Checkout Order
      .addCase(checkoutOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkoutOrder.fulfilled, (state, action) => {
        state.loading = false;
        // Update the order with checkout information
        const orderIndex = state.orders.findIndex(order => order.id === action.payload.order_id);
        if (orderIndex !== -1) {
          state.orders[orderIndex] = { ...state.orders[orderIndex], ...action.payload };
        }
      })
      .addCase(checkoutOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Submit Rating
      .addCase(submitRating.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitRating.fulfilled, (state, action) => {
        state.loading = false;
        // Update the order item in currentOrder if it exists
        if (state.currentOrder && state.currentOrder.items) {
          // The response has order_item (FK) which is the order_item_id
          const orderItemId = action.payload.order_item;
          const itemIndex = state.currentOrder.items.findIndex(
            item => (item.order_item_id || item.id) === orderItemId
          );
          if (itemIndex !== -1) {
            state.currentOrder.items[itemIndex].rated = true;
            state.currentOrder.items[itemIndex].rating = {
              matches_description: action.payload.matches_description,
              created_at: action.payload.created_at
            };
          }
        }
      })
      .addCase(submitRating.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Submit Feedback (star rating)
      .addCase(submitFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitFeedback.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentOrder && state.currentOrder.items) {
          const orderItemId = action.payload.order_item;
          const idx = state.currentOrder.items.findIndex(
            item => (item.order_item_id || item.id) === orderItemId
          );
          if (idx !== -1) {
            state.currentOrder.items[idx].rated = true;
            state.currentOrder.items[idx].feedback = {
              item_as_described: action.payload.item_as_described,
              seller_responsiveness: action.payload.seller_responsiveness,
              stars: action.payload.stars,
              comment: action.payload.comment,
              created_at: action.payload.created_at,
            };
          }
        }
      })
      .addCase(submitFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(submitItemAcceptance.pending, (state) => {
        state.error = null;
      })
      .addCase(submitItemAcceptance.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(submitDoorPreference.pending, (state) => {
        state.error = null;
      })
      .addCase(submitDoorPreference.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError, clearCurrentOrder } = ordersSlice.actions;
export default ordersSlice.reducer;
